import React, { useEffect, useState, useContext } from 'react';
import { get } from 'lodash';
import Text from '@amzn/meridian/text';
import Column from '@amzn/meridian/column';
import Loader from '@amzn/meridian/loader';
import DataState from '../../Types/DataState';
import Alert from '@amzn/meridian/alert';
import { FormattedMessage } from 'react-intl';
import {
  ContactData,
  ContactMediumType,
} from '../../api/ContactListening/schema';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Heading from '@amzn/meridian/heading';
import { StateContext } from '../../GlobalStateStore';
import ChatMedium from './components/ChatMedium';
import RecordingMedium from './components/RecordingMedium';
import EscalationMedium from './components/EscalationMedium';
import EmailMedium from './components/EmailMedium';
import { Feature, isFeatureEnabled } from '../../config/FeaturesConfig';
import {
  permissionErrKeyword,
  canAccessAgentResourceErrKeyword,
  CSOSErrKeyword,
  missingContactData,
} from './utils/ContactListeningConstants';
import {
  CLClickstream,
  ContactListeningSubTab,
  ContactListeningTab,
} from './utils/ContactListeningHelpers';
import { useIntl } from 'react-intl';
import useCustomTracking from '../../Util/useCustomTracking';
import CoachingEmptyState from '../../Assets/coachingEmptyState.png';
export interface ContactListeningContentProps {
  loadingState: string;
  contactId?: string;
  contactListeningData: ContactData;
  contactListeningParams: any;
  CLClickstream: CLClickstream;
}

const ContactListeningContent: React.FC<ContactListeningContentProps> = ({
  loadingState,
  contactId,
  contactListeningData,
  contactListeningParams,
  CLClickstream,
}: ContactListeningContentProps): React.ReactElement => {
  const intl = useIntl();
  const contactListeningWithChat = isFeatureEnabled(
    Feature.CONTACT_LISTENING_WITH_CHAT
  );
  const contactListeningWithEmail = isFeatureEnabled(
    Feature.CONTACT_LISTENING_WITH_EMAIL
  );
  const contactListeningMultitaskEnabled = isFeatureEnabled(
    Feature.CONTACT_LISTENING_MULTITASK
  );
  const [currentTab, setCurrentTab] = useState(ContactListeningTab.CONTACT);
  const [currentSubTab, setCurrentSubTab] = useState(
    ContactListeningSubTab.PHONE_RECORDING
  );

  let hackyEmailFix = false;
  let content = <></>;

  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState.state;
  const { requesterUsername, isTm } = inspireOptions.data;
  const [EscalationTabTrackingTag, escalationClick] = useCustomTracking(
    'contact-listening-escalation-tab',
    'contact-listening-escalation-tab',
    {
      'data-csa-c-type': 'button',
      'data-csa-c-tag1': CLClickstream.tag1,
      'data-csa-c-tag2': CLClickstream.tag2,
      'data-csa-c-tag3': CLClickstream.tag3,
      'data-csa-c-tag4': CLClickstream.tag4,
      'data-csa-c-tag5': CLClickstream.tag5,
      'data-csa-c-element-slot-id': 'contact-listening-escalation-tab',
      'data-csa-c-contactid': CLClickstream.contactID,
      'data-csa-c-is-tm': isTm,
      'data-csa-c-username': requesterUsername,
    }
  );
  const [ChatHistoryTabTrackingTag, chatHistoryClick] = useCustomTracking(
    'contact-listening-chat-history',
    'contact-listening-chat-history',
    {
      'data-csa-c-type': 'button',
      'data-csa-c-tag1': CLClickstream.tag1,
      'data-csa-c-tag2': CLClickstream.tag2,
      'data-csa-c-tag3': CLClickstream.tag3,
      'data-csa-c-tag4': CLClickstream.tag4,
      'data-csa-c-tag5': CLClickstream.tag5,
      'data-csa-c-element-slot-id': 'contact-listening-chat-history',
      'data-csa-c-contactid': CLClickstream.contactID,
      'data-csa-c-is-tm': isTm,
      'data-csa-c-username': requesterUsername,
    }
  );

  useEffect(() => {
    // default to show the tab that has info first
    if (contactListeningWithChat && contactListeningData?.contact) {
      const contactMediumType = contactListeningData.contact.medium?.type;
      if (currentTab === ContactListeningTab.CONTACT) {
        setCurrentSubTab(
          contactMediumType === ContactMediumType.CHAT
            ? ContactListeningSubTab.CHAT_HISTORY
            : contactMediumType === ContactMediumType.EMAIL
              ? ContactListeningSubTab.EMAIL_TRANSCRIPT
              : ContactListeningSubTab.PHONE_RECORDING
        );
      } else if (currentTab === ContactListeningTab.ESCALATION) {
        setCurrentSubTab(
          contactListeningData.contact.dartEscalationContactList?.[0]?.medium
            ?.type === ContactMediumType.CHAT
            ? ContactListeningSubTab.CHAT_HISTORY
            : contactMediumType === ContactMediumType.EMAIL
              ? ContactListeningSubTab.EMAIL_TRANSCRIPT
              : ContactListeningSubTab.PHONE_RECORDING
        );
      }
    }
  }, [contactListeningData, contactListeningParams, currentTab]);

  if (
    loadingState === DataState.INITIALIZED ||
    loadingState === DataState.LOADING
  ) {
    return (
      <Column
        minWidth='700px'
        maxWidth='700px'
        alignmentHorizontal='center'
        alignmentVertical='center'
      >
        <Loader />
      </Column>
    );
  } else if (loadingState === DataState.ERROR) {
    let alert;
    const errorMessage = get(contactListeningData, 'errors[0].message');
    if (errorMessage) {
      if (
        errorMessage.includes(canAccessAgentResourceErrKeyword) ||
        errorMessage.includes(permissionErrKeyword)
      ) {
        alert = (
          <FormattedMessage
            id='cs_perfportal_contact_listening_no_permission_to_view_contact_details'
            defaultMessage='You do not have the permission to view the details of this contact.'
          />
        );
      } else if (errorMessage.includes(CSOSErrKeyword)) {
        alert = (
          <FormattedMessage
            id='cs_perfportal_contact_listening_csos_unauthorized'
            defaultMessage='Non-CSOS : Unauthorized'
          />
        );
      } else if (errorMessage.includes(missingContactData)) {
        alert = (
          <FormattedMessage
            id='cs_perfportal_contact_listening_missing_contact_data'
            defaultMessage="Per customer's privacy request, this contact's recording/transcript has been deleted. There is no data to review."
          />
        );
      }
    }

    if (!alert) {
      alert = (
        <FormattedMessage
          id='cs_perfportal_error_refresh_page'
          defaultMessage='Something went wrong. Please try refreshing the page.
              If the problem persists, please submit a SIM ticket for us to troubleshoot.'
        />
      );
    }

    // temporary hacky fix
    const contactType = get(contactListeningData, 'contact.medium.type');
    if (contactType === 'EMAIL' && contactListeningWithEmail) {
      // is email contact + email enabled
      hackyEmailFix = true;
    } else {
      content = (
        <Column minWidth='700px' maxWidth='700px'>
          <Alert type='error'>{alert}</Alert>
        </Column>
      );
    }
  }
  if (
    hackyEmailFix ||
    loadingState === DataState.SUCCESS ||
    loadingState === DataState.LOADED
  ) {
    if ((!contactListeningData || !contactListeningData.contact) && !content) {
      content = (
        <Column alignmentHorizontal='center' alignmentVertical='center'>
          <div>
            <img src={CoachingEmptyState} alt='no contacts' />
          </div>
          <Text>
            <FormattedMessage
              id='cs_perfportal_contact_listening_no_contact_details'
              defaultMessage='There are no contact details to display here at the moment.'
            />
          </Text>
        </Column>
      );
    } else {
      const noEscalation =
        !contactListeningData.contact?.dartEscalationContactList ||
        contactListeningData.contact?.dartEscalationContactList.length === 0;
      if (!contactListeningWithChat) {
        content = (
          <Column
            minWidth='700px'
            maxWidth='700px'
            data-cy-tab={currentTab}
            data-cy-subtab={currentSubTab}
            data-cy-escalation={!noEscalation}
          >
            {contactId && !contactListeningMultitaskEnabled && (
              <Heading id='tablist-heading' level={3}>
                {`${intl.formatMessage({
                  id: 'cs_perfportal_contact_listening_contact',
                  defaultMessage: 'Contact',
                })} - ${contactId}`}
              </Heading>
            )}
            <TabGroup
              value={currentTab}
              onChange={val => {
                setCurrentTab(val);
                if (currentTab === ContactListeningTab.ESCALATION) {
                  escalationClick();
                }
              }}
              aria-labelledby='tablist-heading'
              type='classic'
              fill='line'
            >
              <Tab
                value={ContactListeningTab.CONTACT}
                aria-controls='tabpanel-contact'
              >
                <FormattedMessage
                  id='cs_perfportal_contact_listening_contact'
                  defaultMessage='Contact'
                />
              </Tab>
              {!noEscalation && (
                <Tab
                  value={ContactListeningTab.ESCALATION}
                  aria-controls='tabpanel-escalation'
                >
                  <FormattedMessage
                    id='cs_perfportal_contact_listening_escalation'
                    defaultMessage='Escalation'
                  />
                </Tab>
              )}
            </TabGroup>
            {currentTab === ContactListeningTab.CONTACT && (
              <div
                role='tabpanel'
                id='tabpanel-phone-recording'
                aria-labelledby='tab-phone-recording'
              >
                <RecordingMedium
                  contact={contactListeningData.contact}
                  contactListeningParams={contactListeningParams}
                  isEscalation={false}
                  CLClickstream={CLClickstream}
                />
              </div>
            )}
            {currentTab === ContactListeningTab.ESCALATION && (
              <div
                role='tabpanel'
                id='tabpanel-escalation-phone-recording'
                aria-labelledby='tab-escalation-phone-recording'
              >
                <EscalationMedium
                  contacts={
                    contactListeningData.contact
                      ? contactListeningData.contact.dartEscalationContactList
                      : undefined
                  }
                  contactListeningParams={contactListeningParams}
                  CLClickstream={CLClickstream}
                  contactType={ContactMediumType.PHONE}
                />
              </div>
            )}
            <EscalationTabTrackingTag />
            <ChatHistoryTabTrackingTag />
          </Column>
        );
      } else {
        content = (
          <Column
            minWidth='700px'
            maxWidth='700px'
            data-cy-tab={currentTab}
            data-cy-subtab={currentSubTab}
            data-cy-escalation={!noEscalation}
          >
            {contactId && !contactListeningMultitaskEnabled && (
              <Heading id='tablist-heading' level={3}>
                {`${intl.formatMessage({
                  id: 'cs_perfportal_contact_listening_contact',
                  defaultMessage: 'Contact',
                })} - ${contactId}`}
              </Heading>
            )}
            <TabGroup
              value={currentTab}
              onChange={val => {
                setCurrentTab(val);
                if (val === ContactListeningTab.ESCALATION) {
                  escalationClick();
                }
              }}
              aria-labelledby='tablist-heading'
              type='classic'
              fill='line'
            >
              <Tab
                value={ContactListeningTab.CONTACT}
                aria-controls='tabpanel-contact'
              >
                <FormattedMessage
                  id='cs_perfportal_contact_listening_contact'
                  defaultMessage='Contact'
                />
              </Tab>
              {!noEscalation && (
                <>
                  <Tab
                    value={ContactListeningTab.ESCALATION}
                    aria-controls='tabpanel-escalation'
                  >
                    <FormattedMessage
                      id='cs_perfportal_contact_listening_escalation'
                      defaultMessage='Escalation'
                    />
                  </Tab>
                </>
              )}
            </TabGroup>
            <TabGroup
              value={currentSubTab}
              onChange={val => {
                setCurrentSubTab(val);
                if (val === ContactListeningSubTab.CHAT_HISTORY) {
                  chatHistoryClick();
                }
              }}
              aria-labelledby='tablist-heading'
            >
              <Tab
                value={ContactListeningSubTab.PHONE_RECORDING}
                aria-controls='tabpanel-phone-recording'
              >
                <FormattedMessage
                  id='cs_perfportal_contact_listening_phone_recording'
                  defaultMessage='Phone Recording'
                />
              </Tab>
              <Tab
                value={ContactListeningSubTab.CHAT_HISTORY}
                aria-controls='tabpanel-chat-history'
              >
                <FormattedMessage
                  id='cs_perfportal_contact_listening_chat_history'
                  defaultMessage='Chat History'
                />
              </Tab>
              <Tab
                value={ContactListeningSubTab.EMAIL_TRANSCRIPT}
                aria-controls='tabpanel-email-transcript'
              >
                <FormattedMessage
                  id='cs_perfportal_contact_listening_email_transcript'
                  defaultMessage='Email Transcript'
                />
              </Tab>
            </TabGroup>
            {currentTab === ContactListeningTab.CONTACT &&
              currentSubTab === ContactListeningSubTab.PHONE_RECORDING && (
                <div
                  role='tabpanel'
                  id='tabpanel-phone-recording'
                  aria-labelledby='tab-phone-recording'
                >
                  <RecordingMedium
                    contact={contactListeningData.contact}
                    contactListeningParams={contactListeningParams}
                    isEscalation={false}
                    CLClickstream={CLClickstream}
                  />
                </div>
              )}
            {currentTab === ContactListeningTab.CONTACT &&
              currentSubTab === ContactListeningSubTab.CHAT_HISTORY && (
                <div
                  role='tabpanel'
                  id='tabpanel-chat-history'
                  aria-labelledby='tab-chat-history'
                >
                  <ChatMedium contact={contactListeningData.contact} />
                </div>
              )}
            {currentTab === ContactListeningTab.CONTACT &&
              currentSubTab === ContactListeningSubTab.EMAIL_TRANSCRIPT && (
                <div
                  role='tabpanel'
                  id='tabpanel-email-transcript'
                  aria-labelledby='tabpanel-email-transcript'
                >
                  <EmailMedium contact={contactListeningData.contact} />
                </div>
              )}

            {currentTab === ContactListeningTab.ESCALATION &&
              currentSubTab === ContactListeningSubTab.PHONE_RECORDING && (
                <div
                  role='tabpanel'
                  id='tabpanel-escalation-phone-recording'
                  aria-labelledby='tab-escalation-phone-recording'
                >
                  <EscalationMedium
                    contacts={
                      contactListeningData.contact
                        ? contactListeningData.contact.dartEscalationContactList
                        : undefined
                    }
                    contactListeningParams={contactListeningParams}
                    CLClickstream={CLClickstream}
                    contactType={ContactMediumType.PHONE}
                  />
                </div>
              )}
            {currentTab === ContactListeningTab.ESCALATION &&
              currentSubTab === ContactListeningSubTab.CHAT_HISTORY && (
                <div
                  role='tabpanel'
                  id='tabpanel-escalation-chat-history'
                  aria-labelledby='tab-escalation-chat-history'
                >
                  <EscalationMedium
                    contacts={
                      contactListeningData.contact
                        ? contactListeningData.contact.dartEscalationContactList
                        : undefined
                    }
                    contactListeningParams={contactListeningParams}
                    CLClickstream={CLClickstream}
                    contactType={ContactMediumType.CHAT}
                  />
                </div>
              )}

            {currentTab === ContactListeningTab.ESCALATION &&
              currentSubTab === ContactListeningSubTab.EMAIL_TRANSCRIPT && (
                <div
                  role='tabpanel'
                  id='tabpanel-escalation-email-transcript'
                  aria-labelledby='tab-escalation-email-transcript'
                >
                  <EscalationMedium
                    contacts={
                      contactListeningData.contact
                        ? contactListeningData.contact.dartEscalationContactList
                        : undefined
                    }
                    contactListeningParams={contactListeningParams}
                    CLClickstream={CLClickstream}
                    contactType={ContactMediumType.EMAIL}
                  />
                </div>
              )}
            <EscalationTabTrackingTag />
            <ChatHistoryTabTrackingTag />
          </Column>
        );
      }
    }
  }

  return content;
};
export default ContactListeningContent;
